/* eslint-disable @typescript-eslint/no-explicit-any */
import { create } from 'zustand';

// 1. A generic type for a slice creator.
type SliceCreator<S> = (set: any, get: any, api: any) => S;

// 2. Merge an array (tuple) of object types into one.
type Merge<T extends object[]> = T extends [infer Head, ...infer Tail]
  ? Head & Merge<Tail extends object[] ? Tail : []>
  : object;

// 3. Compute the overlapping keys between two objects.
type Overlap<A extends object, B extends object> = keyof A & keyof B;

// 4. Recursively check that no two slices overlap.
//    - If an overlap is found, return an object with an error message and the conflicting keys.
//    - Otherwise, return `true`.
type CheckNoOverlap<T extends object[]> = T extends [infer Head, ...infer Tail]
  ? Head extends object
    ? Tail extends object[]
      ? Overlap<Head, Merge<Tail>> extends never
        ? CheckNoOverlap<Tail>
        : { error: 'Overlapping keys detected'; conflict: Overlap<Head, Merge<Tail>> }
      : true
    : true
  : true;

// 5. Compute the merged state type from a tuple of slice creators.
type MergedSlicesReturnType<Slices extends SliceCreator<any>[]> = Merge<{
  [K in keyof Slices]: Slices[K] extends SliceCreator<infer S> ? S : never;
}>;

/**
 * A helper that creates a Zustand store from multiple slice creators.
 * It merges all slice states into a single store and uses TypeScript to ensure
 * that no two slices define the same keys. If a key collision occurs, a descriptive
 * compile-time error is generated, helping you pinpoint the conflicting field.
 */
export function createZustandStore<
  Slices extends SliceCreator<any>[],
  // Evaluate the disjointness of the slices' return types.
  R = CheckNoOverlap<{ [K in keyof Slices]: Slices[K] extends SliceCreator<infer S> ? S : never }>
>(
  ...slices: Slices &
    (R extends true
      ? object
      : { __error: 'Overlapping keys detected in slices'; conflict: R extends { conflict: infer C } ? C : never })
) {
  return create<MergedSlicesReturnType<Slices>>()((set, get, api) => {
    const state = {} as MergedSlicesReturnType<Slices>;

    slices.forEach((sliceCreator) => {
      Object.assign(state, sliceCreator(set, get, api));
    });

    return state;
  });
}
